import axios from 'axios'

import { $axios } from '@/shared/api/axios'
import { ServerResponse } from '@/shared/types/common'

import { getAxiosErrorMessage } from '../lib/get-axios-error-message'
import { CallVerifyResult, SmsVerifyResult } from '../types/auth-flow'
import { SignInData, SignInResponseData } from '../types/auth-schema'

type PhoneCheckData = {
	show_captcha: boolean
	is_new_user: boolean
}

type LoginRequest = {
	phone: string
	captcha?: string
	isReset?: boolean
	deviceUuid?: string | null
	type?: 'call_user' | 'event'
}

type SignUpRequest = {
	phone: string
	captcha?: string
	type?: 'call_user' | 'event'
}

const jsonHeaders = { 'Content-Type': 'application/json' }

export const AuthByPhoneApi = {
	async checkPhone(phone: string) {
		const { data } = await $axios.get<ServerResponse<PhoneCheckData>>(
			`/auth/bitrix/login/check?phone=${encodeURIComponent(phone)}`
		)
		return data
	},

	async login({ phone, captcha, isReset, deviceUuid, type }: LoginRequest) {
		const { data } = await $axios.post<ServerResponse<SignInData>>(
			'auth/bitrix/login',
			{
				phone,
				...(type && { type }),
				...(deviceUuid && { device_uuid: deviceUuid }),
				...(captcha && { recaptcha_response: captcha }),
				...(isReset && { is_reset: true })
			},
			{ headers: jsonHeaders }
		)
		return data.data
	},

	async signUp({ phone, captcha, type }: SignUpRequest) {
		const { data } = await $axios.post<ServerResponse<SignInData>>(
			'auth/bitrix/registration',
			{
				phone,
				...(captcha && { recaptcha_response: captcha }),
				...(type && { type })
			},
			{ headers: jsonHeaders }
		)
		return data.data
	},

	async verifyCall(phone: string, signal?: AbortSignal): Promise<CallVerifyResult> {
		try {
			const { data } = await $axios.post<ServerResponse<SignInResponseData>>(
				'auth/bitrix/call/verify',
				{ phone },
				{ headers: jsonHeaders, timeout: 120000, signal }
			)

			if (data.code === 408 || data.status === 'error') {
				return { status: 'failed' }
			}

			return { status: 'ok', tokens: data.data }
		} catch (error) {
			if (axios.isCancel(error)) {
				return { status: 'aborted' }
			}
			return { status: 'failed' }
		}
	},

	async verifySms(payload: { code: string; signedData: string; uri: string }): Promise<SmsVerifyResult> {
		try {
			const { data } = await $axios.post<ServerResponse<SignInResponseData>>(
				'auth/bitrix/sms/verify',
				{ code: payload.code, signed_data: payload.signedData, uri: payload.uri },
				{ headers: jsonHeaders }
			)

			if (data.code === 500) {
				return { status: 'fatal' }
			}
			if (data.status === 'error') {
				return { status: 'wrong_code', message: data.errors?.[0]?.message ?? 'Неверный код' }
			}

			return { status: 'ok', tokens: data.data }
		} catch (error) {
			if (!axios.isAxiosError(error) || !error.response) {
				return { status: 'network', message: 'У нас что-то сломалось. Попробуйте ещё раз' }
			}

			const status = error.response.status
			if (status >= 500) {
				return { status: 'fatal' }
			}
			if (status === 401) {
				return { status: 'wrong_code', message: getAxiosErrorMessage(error, 'Неверный код') }
			}

			return { status: 'network', message: getAxiosErrorMessage(error, 'У нас что-то сломалось. Попробуйте ещё раз') }
		}
	}
}
